// Every message between client and server. Both sides are type-checked against this file.
//
// Two delivery classes, so the game can move to WebTransport without touching game code:
//   reliable   - ordered, never lost: join, chat, entity info, errors, zone changes, (later) inventory and combat events
//   unreliable - may be lost or reordered: input bundles and snapshots. Each one is self-contained:
//                inputs repeat the last few ticks, and snapshots are deltas against a snapshot the client has
//                confirmed receiving, never against "the previous one sent".
import type { Channel } from './chat.js';
import type { Look } from './look.js';
import type { MoveState } from './sim.js';
import type { InvSlot } from './items.js';
import type { SkyOverride, Weather } from './time.js';
import type { QuestView } from './quests.js';
import type { OfferKind, PartyOp, PartyView, RecruitRow, SeekerRow } from './party.js';
import type { BattleEvent, BattleView, CircleView, Cmd, FormationId } from './battle.js';
import type { JobId } from './jobs.js';

export type EntityKind = 'player' | 'npc' | 'mob' | 'object';

/** Rarely changes: sent reliably when an entity first comes into view. */
export interface EntityInfo {
  id: number;
  kind: EntityKind;
  name: string;
  faction: number;
  look?: Look;
  mob?: string;           // creatures: which kind (shared/mobs.ts)
  hp?: number; max?: number;
}

/** Changes often: carried in snapshots. Compact on purpose (it becomes ~10 bytes in the binary codec). */
export interface EntityState {
  id: number;
  x: number; y: number;   // sub-pixels
  dir: number;            // Dir
  anim: number;           // Anim
  t: number;              // ticks into the current action (attack frame timing)
  z?: number;             // height above the ground in sub-pixels (jumping, flying); left out when 0
  m?: number;             // 1 riding, 2 flying; left out on foot
}

export interface ZoneInfo { id: string; map: string; name: string; instance: boolean; layer: number }

// ---------- client → server ----------
export type ClientMsg =
  | { type: 'hello'; v: number; character: number; quiet?: boolean }   // the account comes from the sign-in cookie; quiet: prefer quieter layers
  | { type: 'input'; seq: number; inputs: number[]; ack: number }   // inputs[i] is tick seq - (len-1-i)
  | { type: 'chat'; ch: Channel; text: string; to?: string }
  | { type: 'ping'; t: number }
  | { type: 'leave' }
  | { type: 'unqueue' }
  // every request that changes saved data carries a client-made id, so a retry is done once
  | { type: 'inv_op'; req: string; op: 'move'; from: number; to: number; qty?: number }
  | { type: 'inv_op'; req: string; op: 'destroy'; slot: number; qty?: number }
  | { type: 'inv_op'; req: string; op: 'equip'; from: number }                 // wear an item from the bag
  | { type: 'inv_op'; req: string; op: 'use'; slot: number }                   // eat food, drink a potion (not in battle)
  | { type: 'inv_op'; req: string; op: 'unequip'; slot: number; to?: number }  // take off what's in equipment slot `slot`
  | { type: 'notes'; text: string }
  | { type: 'photo'; names: string[] }                                       // the sightseeing camera photographed these people
  | { type: 'friend'; op: 'add'; name: string } | { type: 'friend'; op: 'remove'; char: number }
  | { type: 'friend'; op: 'answer'; offer: number; yes: boolean } | { type: 'friend'; op: 'join'; char: number } | { type: 'friend'; op: 'visit'; char: number }   // join: hop onto their layer; visit: their home                                          // your own journal notes
  | { type: 'interact'; req: string; target: string }
  | ({ type: 'party' } & PartyOp)
  // developer tools (only answered for developer accounts): simulated players for stress tests
  | { type: 'dev'; op: 'sim'; sim: SimSettings }
  | { type: 'dev'; op: 'stats' }
  | { type: 'dev'; op: 'sky'; hour: number | null; weather: Weather | null }   // pin the time or weather for everyone (testing)
  | { type: 'dev'; op: 'go'; zone: string }
  // in a battle: a command on your turn, or (any time) cancel running, toggle the help flag, or leave as a helper
  | { type: 'battle'; cmd: Cmd | 'cancel_run' | 'help' | 'leave'; target?: number; skill?: string }
  | { type: 'formation'; id: FormationId }                                   // the party leader (or someone solo) picks the formation
  | { type: 'job'; id: JobId }                                               // change job (in the Guild Hall or at home)
  | { type: 'dev'; op: 'companions'; count: number };                        // simulated party members who follow you and fight with you                                   // jump to a zone (testing)   // pin the time or weather for everyone (testing)

// ---------- server → client ----------
export type ServerMsg =
  | { type: 'welcome'; you: number; character: number; zone: ZoneInfo; tick: number; state: MoveState; seq: number; resumed: boolean; faction: number; name: string; look: Look; dev: boolean; now: number; sky: SkyOverride | null }
  | { type: 'error'; code: 'version' | 'full' | 'bad_hello' | 'auth' | 'rate' | 'kicked' | 'not_yet' | 'no_target' | 'party'; msg: string }
  | { type: 'inv'; size: number; slots: InvSlot[]; gold: number; equip: InvSlot[] }
  | { type: 'inv_update'; req?: string; set: InvSlot[]; clear: number[]; gold?: number; eq?: { set: InvSlot[]; clear: number[] } }
  | { type: 'quests'; list: QuestView[] }
  | { type: 'quest_update'; quest: QuestView }
  | { type: 'journal'; entries: JournalEntry[]; notes: string }
  | { type: 'journal_add'; entry: JournalEntry }
  | { type: 'req_fail'; req: string; msg: string }
  | { type: 'skills'; xp: Record<string, number> }
  | { type: 'skill_xp'; skill: string; xp: number; gained: number }
  | { type: 'nodes'; cooling: [string, number][] }        // personal forage timers: [node id, seconds left]
  | { type: 'notice'; text: string; kind: 'loot' | 'info' | 'level' }
  | { type: 'info'; ents: EntityInfo[] }
  | { type: 'snap'; id: number; base: number; tick: number; ack: number; you: MoveState; ents: EntityState[]; gone: number[] }
  | { type: 'chat'; ch: Channel; from: number; name: string; faction: number; text: string; to?: string }
  | { type: 'pong'; t: number; tick: number; now: number }   // now: the server's clock, for the shared day and weather
  | { type: 'sky'; sky: SkyOverride | null }
  | { type: 'zone'; zone: ZoneInfo; state: MoveState }
  // groups: your party (null when solo), your alliance's parties (yours included, the alliance leader's first),
  // and your own "looking for a party" note (null when the flag is down)
  | { type: 'party_state'; party: PartyView | null; alliance: PartyView[] | null; seeking: string | null }
  | { type: 'party_offer'; offer: number; kind: OfferKind; from: number; name: string; faction: number; size: number; secs: number }
  | { type: 'party_offer_gone'; offer: number }
  | { type: 'finder'; seekers: SeekerRow[]; parties: RecruitRow[] }
  | { type: 'dev_state'; sim: SimSettings; simCount: number; simMax: number; stats: DevStats; layers: Record<string, number[]> }
  // the server is full: you're in line. etaSec is null until people have been seen leaving
  | { type: 'queue'; position: number; size: number; etaSec: number | null }
  | { type: 'friends'; list: FriendView[] }
  | { type: 'friend_update'; friend: FriendView }
  | { type: 'friend_gone'; char: number }
  | { type: 'friend_offer'; offer: number; from: number; name: string; faction: number }
  // combat: [target, amount, hp after, source, flags (HIT in shared/mobs.ts)] for everything you can see this tick
  | { type: 'hits'; list: [number, number, number, number, number][] }
  | { type: 'hp'; hp: number; max: number; mp?: number; maxMp?: number }
  | { type: 'job'; id: JobId; level: number }
  | { type: 'food'; id: string | null; until: number }                       // the meal buff you have (null: none)
  // turn-based battles: the whole state (small) whenever it changes, with what just happened
  | { type: 'battle'; battle: BattleView; events: BattleEvent[]; start?: boolean }
  | { type: 'battle_end'; id: number; result: 'win' | 'lose' | 'fled' | 'left'; text: string }
  // battle circles in your zone (can: you may walk in)
  | { type: 'circles'; list: (CircleView & { can: boolean; mine: boolean })[] }
  | { type: 'formation'; id: FormationId; companions: number };

/** A friend as your list shows them. layer is 0 when offline. */
export interface FriendView { char: number; name: string; faction: number; online: boolean; zone: string; layer: number; home?: boolean }

export interface JournalEntry { at: number; kind: string; ref: string; text: string }
/** world: spread over every zone, not just yours. fight: simulated players in the open world hunt creatures. */
export interface SimSettings { count: number; chatter: boolean; dash: boolean; stall: boolean; spread: boolean; overfill?: boolean; world?: boolean; fight?: boolean }
export interface DevStats { battles: number; mobs: number; hitsPerSec: number; players: number; tickMs: { p50: number; p99: number; max: number }; cpuPercent: number; outKBps: number; msgsPerSec: number; memoryMB: number; lateResets: number }

export type AnyMsg = ClientMsg | ServerMsg;

/** Which delivery class a message uses. Everything not listed is reliable. */
export const UNRELIABLE = new Set<AnyMsg['type']>(['input', 'snap']);
